import { UzButton, UzInput } from '@utazzo/ui';
import React, { useEffect, useState } from 'react';

import { MEDIA_FOLDER, MediaFolderProvider } from '../website-cms/components/media-folder';
import { MediaDropField } from '../website-cms/components/MediaDropField';

import { hexToHslChannels, hslChannelsToHex } from './color';
import { type AppConfigEntryInput } from './settings.api';
import { useAppConfig, useUpdateAppConfig } from './use-settings';

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[12px] text-(--fg-faint)">{hint}</p>}
  </div>
);

const ColorField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}): React.JSX.Element => (
  <Field label={label}>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-12 cursor-pointer rounded border border-(--border) bg-transparent"
      />
      <UzInput value={value} onChange={(e) => onChange(e.target.value)} className="font-mono" />
    </div>
  </Field>
);

export const AppBrandingTab = ({ canWrite = true }: { canWrite?: boolean }): React.JSX.Element => {
  const { data, isLoading } = useAppConfig();
  const save = useUpdateAppConfig();

  const [appName, setAppName] = useState('');
  const [shortName, setShortName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [logoMediaId, setLogoMediaId] = useState<string | null>(null);
  const [iconMediaId, setIconMediaId] = useState<string | null>(null);
  const [logoBgColor, setLogoBgColor] = useState<string | null>(null);
  const [primary, setPrimary] = useState('#093f51');
  const [accent, setAccent] = useState('#def4f7');
  const [fontSans, setFontSans] = useState('');
  const [fontHeading, setFontHeading] = useState('');

  useEffect(() => {
    if (!data) return;
    const light = (data.theme.colors.light ?? {}) as Record<string, string>;
    const fonts = (data.theme.fonts ?? {}) as Record<string, string>;
    setAppName(data.branding.appName ?? '');
    setShortName(data.branding.shortName ?? '');
    setSupportEmail(data.branding.supportEmail ?? '');
    setSupportPhone(data.branding.supportPhone ?? '');
    setLogoBgColor(data.branding.logoBgColor ?? null);
    if (light.primary) setPrimary(hslChannelsToHex(light.primary));
    if (light.accent) setAccent(hslChannelsToHex(light.accent));
    setFontSans(fonts.sans ?? '');
    setFontHeading(fonts.heading ?? '');
  }, [data]);

  if (isLoading || !data) return <p className="p-6 text-sm text-(--fg-faint)">Loading…</p>;

  const onSave = (): void => {
    const light = { ...((data.theme.colors.light ?? {}) as Record<string, string>) };
    light.primary = hexToHslChannels(primary);
    light.accent = hexToHslChannels(accent);
    const fonts = { ...((data.theme.fonts ?? {}) as Record<string, string>) };
    fonts.sans = fontSans;
    fonts.heading = fontHeading;

    const entries: AppConfigEntryInput[] = [
      { key: 'branding.appName', value: appName },
      { key: 'branding.shortName', value: shortName },
      { key: 'branding.supportEmail', value: supportEmail },
      { key: 'branding.supportPhone', value: supportPhone || null },
      // Always sent (not conditional) so turning the background off persists null.
      { key: 'branding.logoBgColor', value: logoBgColor },
      { key: 'theme.colors.light', value: light },
      { key: 'theme.fonts', value: fonts },
    ];
    if (logoMediaId) entries.push({ key: 'branding.logoMediaId', value: logoMediaId });
    if (iconMediaId) entries.push({ key: 'branding.iconMediaId', value: iconMediaId });
    save.mutate(entries);
  };

  return (
    <MediaFolderProvider folder={MEDIA_FOLDER.config}>
      <div className="flex max-w-2xl flex-col gap-6 py-6">
        <Field label="App name">
          <UzInput value={appName} onChange={(e) => setAppName(e.target.value)} />
        </Field>
        <Field label="Short name">
          <UzInput value={shortName} onChange={(e) => setShortName(e.target.value)} />
        </Field>

        <Field label="App logo" hint="Used on the admin/login surfaces. Served through the CDN.">
          {data.branding.logoUrl && !logoMediaId && (
            <img
              src={data.branding.logoUrl}
              alt="Current logo"
              className="mb-2 h-12 w-auto rounded border border-(--border) bg-(--z-50) p-1"
            />
          )}
          <MediaDropField value={logoMediaId} kind="image" onChange={setLogoMediaId} />
        </Field>

        <Field
          label="Logo background"
          hint="Solid colour painted behind the logo in the expanded sidebar. Turn it on when your logo is white or transparent; leave it off for a transparent background."
        >
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-(--fg-muted)">
              <input
                type="checkbox"
                checked={logoBgColor !== null}
                onChange={(e) => setLogoBgColor(e.target.checked ? '#093f51' : null)}
                className="h-4 w-4 cursor-pointer"
              />
              Use a background colour
            </label>
            {logoBgColor !== null && (
              <>
                <input
                  type="color"
                  value={logoBgColor}
                  onChange={(e) => setLogoBgColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-(--border) bg-transparent"
                />
                <UzInput
                  value={logoBgColor}
                  onChange={(e) => setLogoBgColor(e.target.value)}
                  className="w-36 font-mono"
                />
                {data.branding.logoUrl && (
                  <img
                    src={data.branding.logoUrl}
                    alt="Logo preview"
                    className="h-9 w-auto max-w-[150px] rounded object-contain px-2 py-1"
                    style={{ backgroundColor: logoBgColor }}
                  />
                )}
              </>
            )}
          </div>
        </Field>

        <Field
          label="App icon"
          hint="Square brand mark shown when the sidebar is collapsed. Use a compact, square version of the logo."
        >
          {data.branding.iconUrl && !iconMediaId && (
            <img
              src={data.branding.iconUrl}
              alt="Current icon"
              className="mb-2 h-12 w-12 rounded border border-(--border) bg-(--z-50) object-contain p-1"
            />
          )}
          <MediaDropField value={iconMediaId} kind="image" onChange={setIconMediaId} />
        </Field>

        <Field label="Support email">
          <UzInput value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} />
        </Field>
        <Field label="Support phone">
          <UzInput value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />
        </Field>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <legend className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
            Theme colors
          </legend>
          <ColorField label="Primary" value={primary} onChange={setPrimary} />
          <ColorField label="Accent" value={accent} onChange={setAccent} />
        </fieldset>

        <Field label="Body font" hint="CSS font-family stack, e.g. 'Satoshi', sans-serif">
          <UzInput
            value={fontSans}
            onChange={(e) => setFontSans(e.target.value)}
            className="font-mono"
          />
        </Field>
        <Field label="Heading font">
          <UzInput
            value={fontHeading}
            onChange={(e) => setFontHeading(e.target.value)}
            className="font-mono"
          />
        </Field>

        <div className="flex items-center justify-end gap-3 border-t border-(--border) pt-4">
          {!canWrite && (
            <span className="text-xs text-(--fg-faint)">
              Read-only - you can view but not save.
            </span>
          )}
          <UzButton onClick={onSave} loading={save.isPending} disabled={!canWrite}>
            Save branding &amp; theme
          </UzButton>
        </div>
      </div>
    </MediaFolderProvider>
  );
};
